import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicNav from 'layouts/PublicNav';
import AuthService from 'services/auth';
import Credits from 'services/credits';
import './SelfHostedCheckout.css';

const PRICING_HREF = '/pricing#self-hosted';
const PLANS = ['team', 'business'];
const PERIODS = ['monthly', 'yearly'];

// What a person reads when checkout can't start. Never the server's own text —
// that is written for whoever reads the logs.
const COPY = {
  badLink: 'This checkout link is missing something. Pick your plan again on the pricing page.',
  unavailable: 'Checkout isn’t available right now. Please try again in a few minutes.',
};

/**
 * /self-hosted/checkout?plan=&period=&box=
 *
 * Signed out → the sign-in page, which comes straight back here. Signed in →
 * ask the server for a Stripe Checkout and go there. The page itself only
 * ever shows "Taking you to checkout…" or why it couldn't.
 */
const SelfHostedCheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [failure, setFailure] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!AuthService.isAuthenticated()) {
      navigate(`/signin?returnTo=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      return;
    }

    const params = new URLSearchParams(location.search);
    const plan = params.get('plan') || '';
    const period = params.get('period') || 'monthly';
    const box = params.get('box') || '';
    if (!PLANS.includes(plan) || !PERIODS.includes(period)) {
      setFailure(COPY.badLink);
      return;
    }

    Credits.createSelfHostCheckout({ plan, period, box: box || undefined })
      .then(({ url }) => {
        if (!url) throw new Error('no checkout url');
        window.location.assign(url);
      })
      .catch((err) => {
        setFailure(err?.status === 400 ? COPY.badLink : COPY.unavailable);
      });
  }, [location.pathname, location.search, navigate]);

  return (
    <>
      <Helmet>
        <title>Checkout — Teamy</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="public-page shc-page">
        <PublicNav />
        <main className="shc-main">
          <div className="shc-card" role="status" aria-live="polite">
            {failure ? (
              <>
                <h1 className="shc-title">We couldn’t start checkout</h1>
                <p className="shc-text">{failure}</p>
                <p className="shc-text shc-text--muted">
                  Still stuck? Write to <a href="mailto:support@teamy.town">support@teamy.town</a>.
                </p>
                <div className="shc-actions">
                  <a className="shc-btn shc-btn--solid" href={PRICING_HREF}>Back to pricing</a>
                </div>
              </>
            ) : (
              <>
                <div className="shc-spinner" aria-hidden="true" />
                <h1 className="shc-title">Taking you to checkout…</h1>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default SelfHostedCheckoutPage;
